import { useState } from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'
import { useAdminSellersListQuery } from '../queries/use-admin-sellers-list.query'
import { useSelectedSeller } from '../hooks/use-selected-seller'
import { useSellerFilterStore } from '../stores/seller-filter.store'
import { cn } from '../lib/utils'
import { Button } from './ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command'
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover'

export function SellerSelector() {
  const { sellers, isLoadingSellers, sellersError, refetchSellers } =
    useAdminSellersListQuery()
  const selectedSeller = useSelectedSeller()
  const handleSelectSeller = useSellerFilterStore(
    (state) => state.setSelectedSeller,
  )
  const [sellerSelectorOpen, setSellerSelectorOpen] = useState(false)
  const selectedSellerName = sellers.find(
    (seller) => String(seller.codigo) === selectedSeller,
  )?.vendedor
  return (
    <div className="space-y-1 pb-3 max-w-(--breakpoint-2xl) mx-auto">
      <label htmlFor="supervisor-vendedor" className="text-sm font-medium">
        Vendedor
      </label>
      <Popover open={sellerSelectorOpen} onOpenChange={setSellerSelectorOpen}>
        <PopoverTrigger asChild>
          <Button
            id="supervisor-vendedor"
            type="button"
            variant="outline"
            aria-expanded={sellerSelectorOpen}
            className="w-full justify-between font-normal"
            disabled={
              !selectedSeller && (isLoadingSellers || sellers.length === 0)
            }
          >
            <span className="truncate">
              {selectedSellerName ||
                (isLoadingSellers
                  ? 'Carregando vendedores...'
                  : sellersError && sellers.length === 0
                    ? 'Não foi possível carregar os vendedores'
                    : sellers.length === 0
                      ? 'Nenhum vendedor encontrado'
                      : 'Selecione um vendedor')}
            </span>
            <ChevronsUpDown
              className="ml-2 h-4 w-4 shrink-0 opacity-50"
              aria-hidden="true"
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[var(--radix-popover-trigger-width)] p-0"
        >
          <Command
            filter={(_value, search, keywords) => {
              const normalize = (text: string) =>
                text
                  .normalize('NFD')
                  .replace(/\p{M}/gu, '')
                  .toLocaleLowerCase('pt-BR')
                  .replace(/[^\p{L}\p{N}]/gu, '')
              return normalize(keywords?.[0] ?? '').includes(
                normalize(search.trim()),
              )
                ? 1
                : 0
            }}
          >
            <CommandInput
              placeholder="Buscar vendedor pelo nome..."
              aria-label="Buscar vendedor pelo nome"
            />
            <CommandList>
              <CommandEmpty>Nenhum vendedor encontrado.</CommandEmpty>
              <CommandGroup>
                {sellers.map((seller) => (
                  <CommandItem
                    key={seller.codigo}
                    value={String(seller.codigo)}
                    keywords={[seller.vendedor]}
                    onSelect={() => {
                      handleSelectSeller(String(seller.codigo))
                      setSellerSelectorOpen(false)
                    }}
                  >
                    <Check
                      aria-hidden="true"
                      className={cn(
                        'mr-2 h-4 w-4 shrink-0',
                        selectedSeller === String(seller.codigo)
                          ? 'opacity-100'
                          : 'opacity-0',
                      )}
                    />
                    <span>{seller.vendedor}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
          <div className="border-t p-1">
            <Button
              type="button"
              variant="ghost"
              className="w-full justify-start font-normal"
              onClick={() => {
                handleSelectSeller('')
                setSellerSelectorOpen(false)
              }}
            >
              Todos os vendedores
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      {Boolean(sellersError) && (
        <div className="space-y-2">
          <p role="alert" className="text-sm text-destructive">
            Não foi possível atualizar a lista de vendedores.
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={() => void refetchSellers()}
          >
            Tentar novamente
          </Button>
        </div>
      )}
    </div>
  )
}
